import React from 'react';
import { AlertCircle, CheckCircle, AlertTriangle, X } from 'react-feather';
import { useResponsive } from '../hooks/useResponsive';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'success';
  isLoading?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'info',
  isLoading = false,
}) => {
  const { isMobile } = useResponsive();
  
  if (!isOpen) return null;

  const variantConfig = {
    danger: {
      color: '#DC2626',
      background: 'rgba(220, 38, 38, 0.1)',
      icon: <AlertCircle size={40} color="#DC2626" />,
      buttonBg: '#DC2626',
      buttonHoverBg: '#B91C1C',
    },
    warning: {
      color: '#F59E0B',
      background: 'rgba(245, 158, 11, 0.1)',
      icon: <AlertTriangle size={40} color="#F59E0B" />,
      buttonBg: '#F59E0B',
      buttonHoverBg: '#D97706',
    },
    info: {
      color: '#3B82F6',
      background: 'rgba(59, 130, 246, 0.1)',
      icon: <AlertCircle size={40} color="#3B82F6" />,
      buttonBg: '#3B82F6',
      buttonHoverBg: '#2563EB',
    },
    success: {
      color: '#10B981',
      background: 'rgba(16, 185, 129, 0.1)',
      icon: <CheckCircle size={40} color="#10B981" />,
      buttonBg: '#10B981',
      buttonHoverBg: '#059669',
    },
  };

  const config = variantConfig[variant];

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10002,
        padding: '20px',
        animation: 'fadeIn 0.2s ease-out',
      }}
      onClick={onClose}
    >
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .confirm-dialog-content {
            animation: slideUp 0.3s ease-out;
          }
          .confirm-button:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
          .cancel-button:hover {
            background: #F3F4F6;
          }
        `}
      </style>
      <div
        className="confirm-dialog-content"
        style={{
          width: '100%',
          maxWidth: isMobile ? 'calc(100vw - 32px)' : '440px',
          background: 'white',
          borderRadius: isMobile ? '12px' : '16px',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px 24px 0 24px',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '32px',
                background: config.background,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
              }}
            >
              {config.icon}
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              color: isLoading ? '#9CA3AF' : '#6B7280',
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            padding: '0 24px 24px 24px',
          }}
        >
          <h3
            style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#1F2937',
              margin: '0 0 8px 0',
            }}
          >
            {title}
          </h3>
          <p
            style={{
              fontSize: '15px',
              color: '#6B7280',
              lineHeight: '22px',
              margin: '0 0 24px 0',
            }}
          >
            {message}
          </p>

          {/* Action Buttons */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              flexDirection: 'row-reverse',
            }}
          >
            <button
              onClick={handleConfirm}
              disabled={isLoading}
              className="confirm-button"
              style={{
                flex: 1,
                padding: '12px 24px',
                background: isLoading ? '#9CA3AF' : config.buttonBg,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {isLoading ? (
                <>
                  <div
                    style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTopColor: 'white',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite',
                    }}
                  />
                  <style>
                    {`
                      @keyframes spin {
                        to { transform: rotate(360deg); }
                      }
                    `}
                  </style>
                  Processing...
                </>
              ) : (
                confirmText
              )}
            </button>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="cancel-button"
              style={{
                flex: 1,
                padding: '12px 24px',
                background: 'white',
                color: '#374151',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isLoading ? 0.5 : 1,
              }}
            >
              {cancelText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
